import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIButton from '@mui/material/Button';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import CenterFlexBox from 'components/CenterFlexBox';

const ROW1_CARDS = [
    {
        id: 1,
        caption: 'Real Test-Like Environment!',
        src: 'https://assets.languify.in/images/ielts_feature1.jpg',
    },
    {
        id: 2,
        caption: 'Instant, detailed feedback powered by AI trained on IELTS criteria.',
        src: 'https://assets.languify.in/images/ielts_feature2.jpg',
    },
    {
        id: 3,
        caption: 'Timed listening and reading practice with AI-powered speaking evaluation',
        src: 'https://assets.languify.in/images/ielts_feature3.jpg',
    },
];

const ROW2_CARDS = [
    {
        id: 4,
        caption: 'Free Study Material',
        src: 'https://assets.languify.in/images/ielt_feature4.jpg',
    },
    {
        id: 5,
        caption: 'Progress Dashboard',
        src: 'https://assets.languify.in/images/ielts_feature5.jpg',
    },
];

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: '64px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            padding: '48px 16px',
        },
    },

    // ── Header ──────────────────────────────────────────
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '20px',
    },
    badgeStar: {
        color: '#EE634E',
        fontSize: '0.8rem',
    },
    badgeText: {
        color: '#EE634E',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.05em',
    },
    heading: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '2.2rem',
        lineHeight: 1.25,
        textAlign: 'center',
        marginBottom: '14px',
        maxWidth: '500px',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.6rem',
        },
    },
    subtitle: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.9rem',
        lineHeight: 1.6,
        textAlign: 'center',
        marginBottom: '36px',
    },

    // ── Grid ────────────────────────────────────────────
    grid: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginBottom: '36px',
    },
    row: {
        display: 'flex',
        gap: '12px',
        width: '100%',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },

    // ── Image Card ──────────────────────────────────────
    card: {
        flex: 1,
        position: 'relative',
        borderRadius: '14px',
        overflow: 'hidden',
        backgroundColor: '#333651',
        // Row heights controlled inline per row
    },
    // Actual image — use <img> with this class once src is provided
    cardImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    // Placeholder shown until real image is provided
    cardPlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#333651',
    },
    // Dark gradient + caption at bottom of card
    captionOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '36px 16px 14px',
        background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 100%)',
    },
    captionText: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
        fontWeight: 500,
        lineHeight: 1.45,
    },

    // ── CTA ─────────────────────────────────────────────
    ctaButton: {
        backgroundColor: '#EE634E',
        color: '#FFFFFF',
        borderRadius: '50px',
        padding: '12px 28px',
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.95rem',
        fontFamily: 'Mulish, sans-serif',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
    ctaArrow: {
        backgroundColor: '#EE634E',
        color: '#FFFFFF',
        borderRadius: '50%',
        minWidth: '44px',
        width: '44px',
        height: '44px',
        padding: 0,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
}));

function ImageCard({ card, height }) {
    const classes = useStyles();

    return (
        <Box className={classes.card} height={height}>
            {card.src
                ? <img src={card.src} alt={card.caption} className={classes.cardImg} />
                : <Box className={classes.cardPlaceholder} />
            }
            <Box className={classes.captionOverlay}>
                <Typography className={classes.captionText}>
                    {card.caption}
                </Typography>
            </Box>
        </Box>
    );
}

function EverythingYouNeed() {
    const classes = useStyles();

    return (
        <Box className={classes.root}>

            {/* Badge */}
            <Box className={classes.badge}>
                <Typography component="span" className={classes.badgeStar}>✳</Typography>
                <Typography component="span" className={classes.badgeText}>
                    Features
                </Typography>
            </Box>

            {/* Heading */}
            <Typography className={classes.heading}>
                Everything You Need to boost your IELTS score
            </Typography>

            {/* Subtitle */}
            <Typography className={classes.subtitle}>
                Real progress, real outcomes — because your growth matters.
            </Typography>

            {/* Image Grid */}
            <Box className={classes.grid}>

                {/* Row 1 — 3 cards */}
                <Box className={classes.row}>
                    {ROW1_CARDS.map(card => (
                        <ImageCard key={card.id} card={card} height="280px" />
                    ))}
                </Box>

                {/* Row 2 — 2 cards */}
                <Box className={classes.row}>
                    {ROW2_CARDS.map(card => (
                        <ImageCard key={card.id} card={card} height="240px" />
                    ))}
                </Box>

            </Box>

            {/* CTA */}
            <CenterFlexBox gap="8px">
                <MUIButton
                    variant="contained"
                    disableElevation
                    component="a"
                    href="/practice#practice-by-tests"
                    className={classes.ctaButton}
                >
                    Check Your IELTS Band for Free
                </MUIButton>
                <MUIButton
                    variant="contained"
                    disableElevation
                    component="a"
                    href="/practice#practice-by-tests"
                    className={classes.ctaArrow}
                >
                    <NorthEastIcon fontSize="small" />
                </MUIButton>
            </CenterFlexBox>

        </Box>
    );
}

export default EverythingYouNeed;
